import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, ChevronRight, CheckCircle2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/contexts/I18nContext";
import { useMember } from "@/contexts/MemberContext";
import { api } from "@/lib/api";

/**
 * Survey banner shown on the public Home page.
 *
 * Reads from `settings` (passed in by HomePage) and is fully admin-controlled:
 *   - settings.survey_enabled        -> renders nothing when false
 *   - settings.survey_url            -> must be present to render
 *   - settings.survey_title_*        -> bilingual headline
 *   - settings.survey_description_*  -> bilingual subhead
 *   - settings.survey_cta_*          -> bilingual button label
 *
 * Additionally shows a "Completed / Completada" badge if the current member
 * has already self-reported completion via /survey.
 */
export function SurveyBanner({ settings }) {
  const { lang, t } = useI18n();
  const { member } = useMember();
  const [completedAt, setCompletedAt] = useState(null);

  useEffect(() => {
    if (!member?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/survey/me`, { params: { member_id: member.id } });
        if (!cancelled) setCompletedAt(data?.completed_at || null);
      } catch (_) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [member?.id]);

  if (!settings?.survey_enabled || !settings?.survey_url) return null;

  const title = (lang === "en" ? settings.survey_title_en : settings.survey_title_es)
    || settings.survey_title_es
    || settings.survey_title_en
    || (lang === "en" ? "Take our survey" : "Tomar encuesta");
  const desc = (lang === "en" ? settings.survey_description_en : settings.survey_description_es)
    || settings.survey_description_es
    || settings.survey_description_en
    || "";
  const cta = (lang === "en" ? settings.survey_cta_en : settings.survey_cta_es)
    || (t?.survey?.takeNow)
    || (lang === "en" ? "Take it now" : "Tomar ahora");

  return (
    <Card
      className="relative overflow-hidden rounded-2xl border-0 ring-1 ring-primary/15 shadow-sm"
      data-testid="home-survey-banner"
    >
      <div
        className="p-4 pr-3"
        style={{
          background:
            "radial-gradient(640px circle at 110% -10%, rgba(200,30,58,0.18) 0%, transparent 55%), linear-gradient(180deg, hsl(var(--card)) 0%, hsl(var(--accent)) 100%)",
        }}
      >
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 ring-1 ring-primary/20">
            <ClipboardList className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-primary">
                {t?.survey?.cardLabel || (lang === "en" ? "Survey" : "Encuesta")}
              </p>
              {completedAt && (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700 ring-1 ring-emerald-200"
                  data-testid="home-survey-completed-badge"
                >
                  <CheckCircle2 className="h-3 w-3" />
                  {lang === "en" ? "Completed" : "Completada"}
                </span>
              )}
            </div>
            <h3 className="font-serif-display text-lg font-semibold leading-tight mt-0.5" data-testid="home-survey-title">
              {title}
            </h3>
            {desc && (
              <p className="mt-1 text-sm text-muted-foreground leading-snug">{desc}</p>
            )}
            <div className="mt-3">
              <Button asChild size="sm" className="rounded-full" data-testid="home-survey-cta">
                <Link to="/survey">
                  {completedAt
                    ? (lang === "en" ? "Open again" : "Abrir de nuevo")
                    : cta}
                  <ChevronRight className="ml-0.5 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
